import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

import {
  formatEventTime,
  formatSpan,
  normalizeEvent,
  spanEndDay,
  upcomingFrom,
} from "src/lib/events";

import styles from "./EventsSection.module.css";

// Three rows. The section is a pointer at the calendar, not a copy of it.
const LIMIT = 3;

const weekday = (date) =>
  date.toLocaleDateString("en-US", { weekday: "short" });

/** True when a span begins and ends inside the same month. */
function endsInSameMonth(event, last) {
  return (
    last &&
    last.getMonth() === event.start.getMonth() &&
    last.getFullYear() === event.start.getFullYear()
  );
}

/**
 * The date column. A span inside one month reads as one numeral pair under one
 * month label; a span that crosses a month can't, so the numeral stays the
 * start date and the full range moves to the meta line.
 */
function dateParts(event) {
  const last = spanEndDay(event);
  const sameMonth = endsInSameMonth(event, last);

  return {
    month: event.start.toLocaleDateString("en-US", { month: "short" }),
    day: sameMonth
      ? `${event.start.getDate()}–${last.getDate()}`
      : String(event.start.getDate()),
    isRange: Boolean(sameMonth),
  };
}

/** The accent-coloured time plus a dim detail — same split as the events page. */
function metaParts(event) {
  const time = event.allDay ? "All day" : formatEventTime(event);
  const last = spanEndDay(event);

  if (last && !endsInSameMonth(event, last)) {
    return { time, detail: formatSpan(event) };
  }

  const detail = last
    ? `${weekday(event.start)}–${weekday(last)}`
    : weekday(event.start);

  return { time, detail };
}

/**
 * The next few things happening away from the transmitter.
 *
 * Same row grammar as the events page's agenda — a date block, a meta line,
 * a title — so a visitor who's seen one recognizes the other. It renders
 * whenever the calendar has something left, on air or not — a station's
 * events are not filler for when the automation is playing.
 *
 * Titles are printed exactly as they come off the calendar. Tidying them is the
 * calendar's job, and the page is where that shows.
 */
function EventsSection({ events: rawEvents = [], generatedAt }) {
  const events = useMemo(
    () => rawEvents.map(normalizeEvent).filter(Boolean),
    [rawEvents]
  );

  // The first render has to agree on both sides, so it uses the server's clock;
  // the visitor's own "today" is only knowable after mount. Same approach as
  // the events page.
  const [today, setToday] = useState(() => new Date(generatedAt));
  useEffect(() => setToday(new Date()), []);

  const upcoming = useMemo(
    () => upcomingFrom(events, today).slice(0, LIMIT),
    [events, today]
  );

  if (!upcoming.length) return null;

  return (
    <section className={styles.section} aria-labelledby="home-events-title">
      <div className={styles.header}>
        <p className={styles.eyebrow}>Events</p>
        <h2 id="home-events-title">Coming up</h2>
        <p className={styles.blurb}>
          Shows, meetings and station happenings.
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.agenda}>
          {upcoming.map((event) => {
            const { month, day, isRange } = dateParts(event);
            const { time, detail } = metaParts(event);

            return (
              <Link href="/events/" key={event.id} className={styles.row}>
                <span className={styles.date}>
                  <span className={styles.weekday}>{weekday(event.start)}</span>
                  <span className={isRange ? styles.dayRange : styles.day}>
                    {day}
                  </span>
                  <span className={styles.month}>{month}</span>
                </span>
                <span className={styles.main}>
                  <span className={styles.meta}>
                    <span className={styles.metaTime}>{time}</span>
                    <span className={styles.metaSep}>·</span>
                    <span className={styles.metaDetail}>{detail}</span>
                  </span>
                  <span className={styles.name}>{event.title}</span>
                </span>
              </Link>
            );
          })}
        </div>

        <div className="text-end">
          <Link href="/events/" className={styles.link}>
            ALL EVENTS &gt;
          </Link>
        </div>
      </div>
    </section>
  );
}

export default EventsSection;
